/**
 * 分析模块 - 学习状况与答题情况分析
 * 独立模块，与 UIController 解耦
 */

// 题目信息接口
interface ProblemInfo {
  name: string;
  description?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
}

// 刷题统计接口
interface QuizStats {
  totalSessions: number;
  totalQuestions: number;
  correctCount: number;
  wrongCount: number;
  lastSessionDate?: string;
}

// 错题记录接口
interface WrongProblem {
  problemId: string;
  wrongCount: number;
  lastWrongDate: string;
}

// 分析结果接口
interface AnalysisResult {
  // 基础统计
  totalProblems: number;
  completedCount: number;
  completionRate: number;
  
  // 难度分布
  difficultyStats: {
    easy: { total: number; completed: number; rate: number };
    medium: { total: number; completed: number; rate: number };
    hard: { total: number; completed: number; rate: number };
  };
  
  // 分类分布
  categoryStats: Record<string, { total: number; completed: number; rate: number }>;
  
  // 刷题情况
  quizStats: QuizStats;
  
  // 错题分析
  wrongProblems: WrongProblem[];
  weakCategories: string[];
  
  // 学习建议
  suggestions: string[];
}

export class AnalysisModule {
  private container: HTMLElement | null = null;
  private problemInfo: Record<string, ProblemInfo> = {};
  
  constructor() {
    console.log('📊 分析模块初始化');
  }
  
  /**
   * 初始化模块
   */
  init(problemInfo: Record<string, ProblemInfo>): void {
    this.problemInfo = problemInfo;
    this.container = document.getElementById('practice-analysis');
    
    if (this.container) {
      this.render();
      console.log('📊 分析模块已挂载');
    }
  }
  
  /**
   * 刷新分析数据
   */
  refresh(): void {
    if (this.container) {
      this.render();
    }
  }
  
  /**
   * 渲染分析页面
   */
  private render(): void {
    if (!this.container) return;
    
    const analysis = this.analyze();
    
    this.container.innerHTML = `
      <div class="analysis-page">
        <!-- 顶部概览卡片 -->
        <div class="analysis-grid-3">
          <!-- 进度统计卡片 -->
          <div class="grid-card progress-card">
            <div class="card-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm3-6c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3z"/>
              </svg>
            </div>
            <div class="card-content">
              <div class="card-value">${analysis.completionRate}%</div>
              <div class="card-label">完成进度</div>
            </div>
          </div>
          
          <!-- 累计答对 -->
          <div class="grid-card success">
            <div class="card-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div class="card-content">
              <div class="card-value">${analysis.quizStats.correctCount}</div>
              <div class="card-label">累计答对</div>
            </div>
          </div>
          
          <!-- 累计答错 -->
          <div class="grid-card warning">
            <div class="card-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <div class="card-content">
              <div class="card-value">${analysis.quizStats.wrongCount}</div>
              <div class="card-label">累计答错</div>
            </div>
          </div>
        </div>
        
        <!-- 第二行 2x2 布局：答题统计 + 难度分布 + 分类掌握 + 错题本 -->
        <div class="analysis-grid-2x2">
          <!-- 答题统计 -->
          ${this.renderAccuracyCard(analysis.quizStats)}
          
          <!-- 难度分布 -->
          <div class="grid-card-large">
            <h3 class="card-title">难度分布</h3>
            <div class="difficulty-bars">
              ${this.renderDifficultyBar('简单', analysis.difficultyStats.easy, 'easy')}
              ${this.renderDifficultyBar('中等', analysis.difficultyStats.medium, 'medium')}
              ${this.renderDifficultyBar('困难', analysis.difficultyStats.hard, 'hard')}
            </div>
          </div>
          
          <!-- 分类掌握情况 -->
          <div class="grid-card-large">
            <h3 class="card-title">分类掌握情况</h3>
            <div class="category-grid">
              ${this.renderCategoryStats(analysis.categoryStats)}
            </div>
          </div>
          
          <!-- 错题本 -->
          <div class="grid-card-large">
            <h3 class="card-title">错题本 ${analysis.wrongProblems.length > 0 ? `<span class="count">(${analysis.wrongProblems.length}题)</span>` : ''}</h3>
            ${analysis.wrongProblems.length > 0 ? `
              <div class="wrong-list">
                ${this.renderWrongProblems(analysis.wrongProblems)}
              </div>
            ` : `
              <div class="empty-state">
                <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" opacity="0.3">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>暂无错题</span>
              </div>
            `}
          </div>
        </div>
        
        <!-- 薄弱环节 -->
        ${analysis.weakCategories.length > 0 ? `
        <div class="analysis-section">
          <h3 class="section-title">薄弱环节</h3>
          <div class="weak-areas">
            ${analysis.weakCategories.map(cat => `
              <span class="weak-tag">${this.getCategoryName(cat)}</span>
            `).join('')}
          </div>
        </div>
        ` : ''}
      </div>
    `;
  }
  
  /**
   * 执行分析
   */
  private analyze(): AnalysisResult {
    const completed = this.getCompletedProblems();
    const quizStats = this.getQuizStats();
    const wrongProblems = this.getWrongProblems();
    
    const totalProblems = Object.keys(this.problemInfo).length;
    const completedCount = completed.length;
    const completionRate = totalProblems > 0 ? Math.round((completedCount / totalProblems) * 100) : 0;
    
    // 难度统计
    const difficultyStats = this.calculateDifficultyStats(completed);
    
    // 分类统计
    const categoryStats = this.calculateCategoryStats(completed);
    
    // 找出薄弱分类（完成率低于30%且有题目的分类）
    const weakCategories = Object.entries(categoryStats)
      .filter(([_, stats]) => stats.total > 0 && stats.rate < 30)
      .map(([cat]) => cat);
    
    // 生成建议
    const suggestions = this.generateSuggestions(difficultyStats, categoryStats, wrongProblems, quizStats);
    
    return {
      totalProblems,
      completedCount,
      completionRate,
      difficultyStats,
      categoryStats,
      quizStats,
      wrongProblems,
      weakCategories,
      suggestions
    };
  }
  
  /**
   * 计算难度统计
   */
  private calculateDifficultyStats(completed: string[]) {
    const stats = {
      easy: { total: 0, completed: 0, rate: 0 },
      medium: { total: 0, completed: 0, rate: 0 },
      hard: { total: 0, completed: 0, rate: 0 }
    };
    
    Object.entries(this.problemInfo).forEach(([id, info]) => {
      const diff = info.difficulty;
      stats[diff].total++;
      if (completed.includes(id)) {
        stats[diff].completed++;
      }
    });
    
    // 计算完成率
    Object.keys(stats).forEach(diff => {
      const d = diff as 'easy' | 'medium' | 'hard';
      stats[d].rate = stats[d].total > 0 
        ? Math.round((stats[d].completed / stats[d].total) * 100) 
        : 0;
    });
    
    return stats;
  }
  
  /**
   * 计算分类统计
   */
  private calculateCategoryStats(completed: string[]) {
    const stats: Record<string, { total: number; completed: number; rate: number }> = {};
    
    Object.entries(this.problemInfo).forEach(([id, info]) => {
      const cat = info.category || 'other';
      if (!stats[cat]) {
        stats[cat] = { total: 0, completed: 0, rate: 0 };
      }
      stats[cat].total++;
      if (completed.includes(id)) {
        stats[cat].completed++;
      }
    });
    
    // 计算完成率
    Object.keys(stats).forEach(cat => {
      stats[cat].rate = stats[cat].total > 0 
        ? Math.round((stats[cat].completed / stats[cat].total) * 100) 
        : 0;
    });
    
    return stats;
  }
  
  /**
   * 生成学习建议
   */
  private generateSuggestions(
    difficultyStats: AnalysisResult['difficultyStats'],
    categoryStats: AnalysisResult['categoryStats'],
    wrongProblems: WrongProblem[],
    quizStats: QuizStats
  ): string[] {
    const suggestions: string[] = [];
    
    // 基于完成情况的建议
    if (difficultyStats.easy.rate < 50) {
      suggestions.push('建议先从简单题目入手，打好基础');
    } else if (difficultyStats.easy.rate >= 80 && difficultyStats.medium.rate < 30) {
      suggestions.push('简单题掌握不错，可以尝试挑战中等难度题目了');
    }
    
    if (difficultyStats.medium.rate >= 60 && difficultyStats.hard.rate < 20) {
      suggestions.push('中等难度掌握良好，可以挑战困难题目提升自己');
    }
    
    // 基于错题的建议
    if (wrongProblems.length > 0) {
      const frequentWrong = wrongProblems.filter(p => p.wrongCount >= 2);
      if (frequentWrong.length > 0) {
        suggestions.push(`有 ${frequentWrong.length} 道题多次答错，建议重点复习错题本`);
      }
    }
    
    // 基于分类的建议
    const weakCats = Object.entries(categoryStats)
      .filter(([_, s]) => s.total > 0 && s.rate < 30)
      .map(([cat]) => this.getCategoryName(cat));
    
    if (weakCats.length > 0) {
      suggestions.push(`${weakCats.join('、')} 类型题目完成率较低，建议加强练习`);
    }
    
    // 基于刷题频率的建议
    if (quizStats.totalSessions === 0) {
      suggestions.push('还没有开始刷题，快去刷题页面挑战自己吧！');
    } else if (quizStats.totalSessions >= 5) {
      const accuracy = quizStats.correctCount + quizStats.wrongCount > 0
        ? Math.round((quizStats.correctCount / (quizStats.correctCount + quizStats.wrongCount)) * 100)
        : 0;
      if (accuracy >= 80) {
        suggestions.push(`刷题正确率 ${accuracy}%，表现优秀！继续保持`);
      } else if (accuracy < 50) {
        suggestions.push('刷题正确率有待提高，建议先复习知识点再刷题');
      }
    }
    
    // 默认建议
    if (suggestions.length === 0) {
      suggestions.push('继续保持学习节奏，每天坚持练习！');
    }
    
    return suggestions;
  }
  
  /**
   * 渲染难度进度条
   */
  private renderDifficultyBar(label: string, stats: { total: number; completed: number; rate: number }, type: string): string {
    return `
      <div class="difficulty-bar-item">
        <div class="bar-label">
          <span class="difficulty-tag ${type}">${label}</span>
          <span class="bar-stats">${stats.completed}/${stats.total}</span>
        </div>
        <div class="bar-track">
          <div class="bar-fill ${type}" style="width: ${stats.rate}%"></div>
        </div>
        <span class="bar-percent">${stats.rate}%</span>
      </div>
    `;
  }
  
  /**
   * 渲染分类统计
   */
  private renderCategoryStats(categoryStats: Record<string, { total: number; completed: number; rate: number }>): string {
    return Object.entries(categoryStats).map(([cat, stats]) => `
      <div class="category-item">
        <div class="category-name">${this.getCategoryName(cat)}</div>
        <div class="category-progress">
          <div class="category-bar">
            <div class="category-fill" style="width: ${stats.rate}%"></div>
          </div>
          <span class="category-rate">${stats.rate}%</span>
        </div>
        <div class="category-count">${stats.completed}/${stats.total}</div>
      </div>
    `).join('');
  }
  
  /**
   * 渲染错题列表
   */
  private renderWrongProblems(wrongProblems: WrongProblem[]): string {
    return wrongProblems.slice(0, 5).map(wp => {
      const info = this.problemInfo[wp.problemId];
      if (!info) return '';
      
      return `
        <div class="wrong-item" data-problem="${wp.problemId}">
          <div class="wrong-info">
            <span class="wrong-name">${info.name}</span>
            <span class="wrong-difficulty ${info.difficulty}">${this.getDifficultyName(info.difficulty)}</span>
          </div>
          <div class="wrong-meta">
            <span class="wrong-count">错误 ${wp.wrongCount} 次</span>
          </div>
        </div>
      `;
    }).join('');
  }
  
  /**
   * 渲染答题统计卡片
   */
  private renderAccuracyCard(quizStats: QuizStats): string {
    const total = quizStats.correctCount + quizStats.wrongCount;
    const correctPercent = total > 0 ? Math.round((quizStats.correctCount / total) * 100) : 0;
    
    return `
      <div class="grid-card-large">
        <h3 class="card-title">答题统计</h3>
        ${total > 0 ? `
          <div class="accuracy-stats">
            <div class="accuracy-main">
              <span class="accuracy-value">${correctPercent}%</span>
              <span class="accuracy-label">正确率</span>
            </div>
            <div class="accuracy-details">
              <div class="accuracy-item correct">
                <span class="accuracy-num">${quizStats.correctCount}</span>
                <span class="accuracy-text">答对</span>
              </div>
              <div class="accuracy-item wrong">
                <span class="accuracy-num">${quizStats.wrongCount}</span>
                <span class="accuracy-text">答错</span>
              </div>
              <div class="accuracy-item total">
                <span class="accuracy-num">${total}</span>
                <span class="accuracy-text">总计</span>
              </div>
            </div>
          </div>
        ` : `
          <div class="empty-state">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" opacity="0.3">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
            </svg>
            <span>暂无答题数据</span>
          </div>
        `}
      </div>
    `;
  }

  /**
   * 渲染答题准确率图表
   */
  private renderAccuracyChart(quizStats: QuizStats): string {
    const total = quizStats.correctCount + quizStats.wrongCount;
    if (total === 0) {
      return `
        <div class="analysis-section chart-section">
          <h3 class="section-title">答题统计</h3>
          <div class="empty-chart">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor" opacity="0.3">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
            </svg>
            <p>暂无答题数据</p>
          </div>
        </div>
      `;
    }
    
    const correctPercent = Math.round((quizStats.correctCount / total) * 100);
    const wrongPercent = 100 - correctPercent;
    
    // 生成环形图的 path
    const correctAngle = (correctPercent / 100) * 360;
    const correctPath = this.describeArc(50, 50, 40, 0, correctAngle);
    const wrongPath = this.describeArc(50, 50, 40, correctAngle, 360);
    
    return `
      <div class="analysis-section chart-section">
        <h3 class="section-title">答题统计</h3>
        <div class="accuracy-chart">
          <div class="donut-chart">
            <svg viewBox="0 0 100 100">
              ${correctPercent > 0 ? `<path d="${correctPath}" class="donut-correct"/>` : ''}
              ${wrongPercent > 0 ? `<path d="${wrongPath}" class="donut-wrong"/>` : ''}
              <circle cx="50" cy="50" r="28" class="donut-center"/>
              <text x="50" y="46" class="donut-value">${correctPercent}%</text>
              <text x="50" y="58" class="donut-label">正确率</text>
            </svg>
          </div>
          <div class="chart-legend">
            <div class="legend-item">
              <span class="legend-dot correct"></span>
              <span class="legend-text">答对</span>
              <span class="legend-value">${quizStats.correctCount} 题</span>
            </div>
            <div class="legend-item">
              <span class="legend-dot wrong"></span>
              <span class="legend-text">答错</span>
              <span class="legend-value">${quizStats.wrongCount} 题</span>
            </div>
            <div class="legend-item total">
              <span class="legend-dot total"></span>
              <span class="legend-text">总计</span>
              <span class="legend-value">${total} 题</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }
  
  /**
   * 生成 SVG 弧形路径
   */
  private describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
    const start = this.polarToCartesian(x, y, radius, endAngle);
    const end = this.polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return `M ${x} ${y} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
  }
  
  private polarToCartesian(cx: number, cy: number, radius: number, angleInDegrees: number) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180;
    return {
      x: cx + (radius * Math.cos(angleInRadians)),
      y: cy + (radius * Math.sin(angleInRadians))
    };
  }
  
  /**
   * 获取分类显示名称
   */
  private getCategoryName(cat: string): string {
    const names: Record<string, string> = {
      'array': '数组',
      'string': '字符串',
      'linked-list': '链表',
      'tree': '树',
      'dp': '动态规划',
      'hash': '哈希表',
      'math': '数学',
      'other': '其他'
    };
    return names[cat] || cat;
  }
  
  /**
   * 获取难度显示名称
   */
  private getDifficultyName(diff: string): string {
    const names: Record<string, string> = {
      'easy': '简单',
      'medium': '中等',
      'hard': '困难'
    };
    return names[diff] || diff;
  }
  
  // ========================================
  // 数据读取方法（从 localStorage）
  // ========================================
  
  private getCompletedProblems(): string[] {
    try {
      const data = localStorage.getItem('completed_problems');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }
  
  private getQuizStats(): QuizStats {
    try {
      const data = localStorage.getItem('quiz_stats');
      if (data) {
        const parsed = JSON.parse(data);
        return {
          totalSessions: 1,
          totalQuestions: (parsed.correct || 0) + (parsed.wrong || 0),
          correctCount: parsed.correct || 0,
          wrongCount: parsed.wrong || 0,
          lastSessionDate: parsed.date
        };
      }
    } catch {}
    
    return {
      totalSessions: 0,
      totalQuestions: 0,
      correctCount: 0,
      wrongCount: 0
    };
  }
  
  private getWrongProblems(): WrongProblem[] {
    try {
      const data = localStorage.getItem('wrong_problems');
      if (data) {
        const ids: string[] = JSON.parse(data);
        // 将简单ID数组转换为WrongProblem格式
        return ids.map(id => ({
          problemId: id,
          wrongCount: 1,
          lastWrongDate: ''
        }));
      }
      return [];
    } catch {
      return [];
    }
  }
}

// 导出单例
export const analysisModule = new AnalysisModule();
